import type { ZudokuConfig } from "zudoku";

const config: ZudokuConfig = {
  site: {
    title: "compose_run",
    logo: {
      src: { light: "/logo-mark.png", dark: "/logo-mark.png" },
      alt: "compose_run",
      width: "32px",
    },
  },
  search: {
    type: "pagefind",
    maxSubResults: 3,
    ranking: {
      termFrequency: 0.8,
      pageLength: 0.6,
      termSimilarity: 1.2,
      termSaturation: 1.2,
    },
  },
  theme: {
    fonts: {
      mono: "JetBrains Mono",
    },
    customCss: `
      @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@500;700&display=swap');

      /* Wordmark "compose_run" in JetBrains Mono next to the logo in the header */
      header a[href="/"] > div::after {
        content: "compose_run";
        font-family: "JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace;
        font-weight: 700;
        font-size: 1.125rem;
        letter-spacing: -0.03em;
        color: var(--foreground);
        white-space: nowrap;
        line-height: 1;
      }
    `,
  },
  metadata: {
    title: "compose_run docs",
    description:
      "Official compose_run documentation — One-Click Docker Compose Hosting, EU-Based. User guides and API reference for the public webhooks.",
    applicationName: "compose_run",
    generator: "Zudoku",
    referrer: "origin-when-cross-origin",
    keywords: [
      "compose_run",
      "docker compose",
      "PaaS",
      "EU hosting",
      "docker hosting",
      "self-hosted",
      "EU sovereign",
      "container hosting",
      "deploy webhooks",
      "CI/CD deploy",
      "documentation",
      "API",
    ],
    authors: ["compose_run"],
    creator: "compose_run",
    publisher: "compose_run",
    logo: "/logo-mark.png",
    favicon: "/favicon.ico",
  },
  canonicalUrlOrigin: "https://docs.composerun.eu",
  sitemap: {
    siteUrl: "https://docs.composerun.eu",
    changefreq: "weekly",
    priority: 0.7,
    autoLastmod: true,
    exclude: ["/404"],
  },
  navigation: [
    {
      type: "category",
      label: "User Guide",
      icon: "book-open",
      items: [
        "/user-guide/welcome",
        "/user-guide/quickstart",
        "/user-guide/concepts",
        {
          type: "category",
          label: "Playground",
          collapsible: true, // optional
          collapsed: false, // optional
          items: ["/user-guide/playground/domain"],
        },
      ],
    },
    {
      type: "category",
      label: "API spec",
      icon: "code",
      items: [
        {
          type: "link",
          icon: "folder-cog",
          badge: { label: "OpenAPI", color: "purple" },
          label: "API Reference",
          to: "/api",
        },
      ],
    },
    {
      type: "link",
      icon: "git-pull-request-arrow",
      label: "Changelog",
      to: "/changelog",
    },
    {
      type: "link",
      icon: "external-link",
      label: "App console",
      to: "https://app.composerun.eu",
    },
  ],
  slots: {
    "layout-before-head": (
      <head>
        <script
          // biome-ignore lint/security/noDangerouslySetInnerHtml: trusted first-party PostHog analytics snippet
          dangerouslySetInnerHTML={{
            __html: `!function(t,e){var o,n,p,r;e.__SV||(window.posthog && window.posthog.__loaded)||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement("script")).type="text/javascript",p.crossOrigin="anonymous",p.async=!0,p.src=s.api_host.replace(".i.posthog.com","-assets.i.posthog.com")+"/static/array.js",(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],u.toString=function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e},u.people.toString=function(){return u.toString(1)+".people (stub)"},o="Ei Ni init zi Gi Nr Ui Xi Hi capture calculateEventProperties tn register register_once register_for_session unregister unregister_for_session an getFeatureFlag getFeatureFlagPayload getFeatureFlagResult isFeatureEnabled reloadFeatureFlags updateFlags updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures on onFeatureFlags onSurveysLoaded onSessionId getSurveys getActiveMatchingSurveys renderSurvey displaySurvey cancelPendingSurvey canRenderSurvey canRenderSurveyAsync ln identify setPersonProperties group resetGroups setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags reset setIdentity clearIdentity get_distinct_id getGroups get_session_id get_session_replay_url alias set_config startSessionRecording stopSessionRecording sessionRecordingStarted captureException addExceptionStep captureLog startExceptionAutocapture stopExceptionAutocapture loadToolbar get_property getSessionProperty nn Qi createPersonProfile setInternalOrTestUser sn qi cn opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing get_explicit_consent_status is_capturing clear_opt_in_out_capturing Ji debug Fr rn getPageViewId captureTraceFeedback captureTraceMetric Bi".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);
posthog.init('phc_CEYPH3m4gqN2RmHhTRxveqXviyeuK78iGW9Cfkkp3kTD', {
    api_host: 'https://docs.composerun.eu/ph',
    defaults: '2026-01-30',
    person_profiles: 'identified_only',
})`,
          }}
        />
      </head>
    ),
  },
  redirects: [{ from: "/", to: "/home" }],
  apis: [
    {
      type: "file",
      input: "./apis/openapi.yaml",
      path: "/api",
      options: {
        disableSecurity: false,
      },
    },
  ],
};

export default config;
